import { alpha, useTheme } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import _ from 'lodash';
import React from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  UseControllerOptions,
  UseFormMethods,
} from 'react-hook-form';
import styled from 'styled-components/macro';
import { useDebounce } from 'use-debounce';
import Icon from '../icon/Icon';
import IconLib from '../icon/icon-lib';
import { AnimatedMarkup } from '../misc';
import { InputPropsFor } from './utilities/input.types';
import {
  SharedInputProps,
  sharedInputStyle,
} from './utilities/shared-input-style';

const StyledDatePicker = styled(KeyboardDatePicker)<SharedInputProps>`
  ${sharedInputStyle}
`;

type Props<F extends FieldValues> = InputPropsFor<'DateInput', F> & {
  setError: UseFormMethods<F>['setError'];
};

export default function FormDateInput<F extends FieldValues>({
  controlName,
  optional,
  rules = {},
  control,
  errors,
  defaults = {},
  classicPlaceholder,
  disabled,
  setError,
  ...props
}: Props<F>): JSX.Element {
  const theme = useTheme();

  const controller: UseControllerOptions = {
    control: control as Control,
    name: controlName,
    defaultValue: defaults[controlName] || null,
    rules: {
      required: optional ? undefined : 'Required',
      ...rules,
    },
  };

  const errorObj = errors[controlName];
  const errorText = _.isArray(errorObj)
    ? errorObj.find(obj => obj?.message)?.message
    : errorObj?.message;

  // NOTE: this stops the (internal) error message flashing when
  // typing, as on each keystroke the error switches to undefined for
  // a brief moment before being set again by 'setError'.
  const [errorTextDebounced] = useDebounce(errorText, 50);
  const iconColor = disabled
    ? alpha('#000', theme.palette.action.disabledOpacity)
    : 'primary';

  const DateFieldComponent = ({
    ref,
    value,
    onChange,
    ...controllerRenderProps
  }: Partial<ControllerRenderProps> = {}) => (
    <StyledDatePicker
      $greyPlaceholder={!classicPlaceholder}
      inputVariant="outlined"
      inputRef={ref}
      format="dd/MM/yyyy"
      disableFuture
      keyboardIcon={
        <Icon icon={IconLib.CALENDAR} size={30} color={iconColor} />
      }
      KeyboardButtonProps={{
        'aria-label': 'change date',
      }}
      onError={error => {
        const internalErrorMsg = String(error ?? '');
        const displayedErrorMsg = errorText ?? '';
        if (!displayedErrorMsg && internalErrorMsg) {
          setError(controlName, {
            type: 'internal',
            message: internalErrorMsg,
            shouldFocus: true,
          });
        }
      }}
      FormHelperTextProps={{
        component: 'div',
      }}
      helperText={
        <AnimatedMarkup>{errorTextDebounced}</AnimatedMarkup>
      }
      error={!!errorTextDebounced}
      value={(value as Date) ?? null}
      onChange={onChange as (date: Date | null) => void}
      {...{ disabled }}
      {...props}
      {...controllerRenderProps}
    />
  );

  return <Controller render={DateFieldComponent} {...controller} />;
}
